import { Check } from "lucide-react";
import Image from "next/image";
import Link from "next/link";
import MaxWidthWrapper from "components/Tiles/commonComponents/MaxWidthWrapper";

type TwoCardsProps = {
  miniTitle: string;
  title?: string;
  subTitle?: string;
  titleTag?: React.ElementType;
  subtitleTag?: React.ElementType;
  bgColor?: string;
  cards?: {
    icon: string;
    title: string;
    content: string;
    image?: string;
    listTitle?: string;
    listItems?: string[];
    offerRange?: string;
    offerRangeLink?: {
      text: string;
      url: string;
    };
    badge?: {
      text: string;
      color?: string;
      textColor?: string;
    };
  }[];
  className?: string;
  topImage?: {
    src: string;
    alt: string;
    width: number;
    height: number;
  };
};

const TwoCards = ({
  miniTitle,
  title,
  subTitle,
  titleTag = "p",
  subtitleTag = "span",
  bgColor,
  cards,
  className,
  topImage
}: TwoCardsProps) => {
  const TitleTag = titleTag;
  const SubTitleTag = subtitleTag;

  const isSingleCard = cards?.length === 1;

  return (
    <section style={{ backgroundColor: bgColor }} id="twocards-99">
      <MaxWidthWrapper className="py-32">
        {topImage && (
          <div className="mb-8 flex justify-center">
            <Image src={topImage.src} alt={topImage.alt} width={topImage.width} height={topImage.height} />
          </div>
        )}
        <div className="mx-auto max-w-[60%] text-center">
          {miniTitle && <SubTitleTag className="subby mb-2">{miniTitle}</SubTitleTag>}
          <TitleTag className="mb-3 font-heading text-[68px] leading-[75px]">{title}</TitleTag>
          {subTitle && <p className="mt-4 text-2xl text-primary-700">{subTitle}</p>}
        </div>
        <div
          className={`mt-24 grid rounded-2xl bg-[#fffdf6] ${
            isSingleCard ? "grid-cols-1" : "grid-cols-1 md:grid-cols-2"
          } lg:gap-0`}
        >
          {cards &&
            cards.map((card, index) => (
              <div
                key={index}
                className={`relative p-4 px-4 py-14 text-center ${
                  !isSingleCard && index === 0 ? "border-r-[1px] border-gray-200" : ""
                }`}
              >
                {card.badge && (
                  <div className="absolute -top-4 left-1/2 -translate-x-1/2 transform">
                    <div
                      className="rounded-full px-6 py-2 font-medium shadow-md"
                      style={{
                        backgroundColor: card.badge.color || "#4F46E5",
                        color: card.badge.textColor || "white"
                      }}
                    >
                      {card.badge.text}
                    </div>
                  </div>
                )}
                <div className="mx-auto max-w-[90%]">
                  {card.icon && (
                    <div className="mb-4 flex justify-center">
                      <Image src={card.icon} alt={`${card.title} icon`} width={64} height={64} />
                    </div>
                  )}
                  <h3 className="mx-4 mb-1 px-12 py-2 text-center font-heading text-[42px] leading-[58px] [text-wrap:balance]">
                    {card.title}
                  </h3>
                  {card.offerRange && (
                    <div className="mb-6">
                      <p className="text-2xl font-semibold text-primary-600">{card.offerRange}</p>
                      {card.offerRangeLink && (
                        <Link
                          href={card.offerRangeLink.url}
                          className="text-primary-500 text-sm underline hover:text-primary-600"
                        >
                          {card.offerRangeLink.text}
                        </Link>
                      )}
                    </div>
                  )}
                  <p className="text-lg">
                    {card.content ||
                      "Lorem ipsum dolor sit amet, consectetur adipiscing elit. Vivamus lacinia odio vitae vestibulum."}
                  </p>
                </div>
                <div className="mt-4 flex items-center">
                  {card.image && (
                    <div className="relative mr-4 h-24 h-[28vw] max-h-[24rem] w-full max-w-[175px]">
                      <Image
                        src={card.image}
                        alt="Card Image"
                        fill
                        style={{
                          objectFit: "contain"
                        }}
                      />
                    </div>
                  )}
                  <div className="mx-auto max-w-[90%] pl-2 text-left">
                    {card.listTitle && <h4 className="text-l text-center font-semibold">{card.listTitle}</h4>}
                    <ul className="list-decimal text-primary-600">
                      {card.listItems?.map((item, idx) => (
                        <li key={idx} className="mt-4 flex items-start">
                          <span className="my-1 -ml-2 mr-2 rounded-full bg-brandlight p-[3px]">
                            <Check size={14} color="#ffffff" />
                          </span>
                          {item}
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </div>
            ))}
        </div>
      </MaxWidthWrapper>
    </section>
  );
};

export default TwoCards;
